import { useTranslation } from 'react-i18next'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Offerers, SchedulingModuleConfig } from '@/types/scheduling'
import type { ModuleFieldComponentProps } from '@/types/space-wizard'
import { FormField } from '@/components/features/bookings/FormField'
import type { SchedulingConfigErrors } from './schedulingConfig'

interface Props extends ModuleFieldComponentProps<SchedulingModuleConfig> {
  errors?: SchedulingConfigErrors | null
}

const OFFERERS: Offerers[] = ['ADMINS', 'TEAM', 'MEMBERS']

/**
 * The Agendamento setup: who may offer hours, whether the community may charge for them and whether a
 * member's offer waits for approval. The wizard asks it as a step of its own (PADROES-UI section 14) and the
 * space edit shows the very same component.
 */
export const SchedulingSpaceFields = ({ config, onChange, disabled = false, errors }: Props) => {
  const { t } = useTranslation('scheduling')

  const toggle = (label: string, hint: string, checked: boolean, onCheckedChange: (on: boolean) => void, testId: string) => (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{hint}</p>
      </div>
      <Switch checked={checked} disabled={disabled} aria-label={label} data-testid={testId} onCheckedChange={onCheckedChange} />
    </div>
  )

  return (
    <div className="space-y-5" data-testid="scheduling-setup">
      <FormField label={t('config.offerers')} htmlFor="sch-offerers" error={errors?.offerers} hint={t(`config.offerersHint.${config.offerers}`)}>
        <Select
          value={config.offerers}
          disabled={disabled}
          /* The API's own default for a member's offer: it waits for approval. */
          onValueChange={(value) => onChange({ ...config, offerers: value as Offerers, requireListingApproval: value === 'MEMBERS' ? true : config.requireListingApproval })}
        >
          <SelectTrigger id="sch-offerers" aria-label={t('config.offerers')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {OFFERERS.map((key) => <SelectItem key={key} value={key}>{t(`config.offerersOption.${key}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </FormField>

      {config.offerers === 'MEMBERS' && toggle(
        t('config.requireApproval'), t('config.requireApprovalHint'), config.requireListingApproval,
        (on) => onChange({ ...config, requireListingApproval: on }), 'scheduling-approval'
      )}

      {toggle(
        t('config.allowPaid'), t('config.allowPaidHint'), config.allowPaidSlots,
        (on) => onChange({ ...config, allowPaidSlots: on }), 'scheduling-paid'
      )}

      <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">{t('config.memberSlotsFree')}</p>
    </div>
  )
}
